import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Minus, Plus, ShoppingCart, ArrowRight, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import { SiteContentProvider } from "../data/SiteContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CART_KEY = "btw_pickup_cart_v4";

const CATEGORY_LABELS = {
  packs: "Curated Packs",
  "half-pans": "Half Pans",
  sides: "Sides",
};

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

const signature = (selections) => {
  if (!selections) return "";
  return Object.keys(selections).sort()
    .map((gid) => {
      const v = selections[gid];
      if (!v) return "";
      // slot shape
      if (typeof v === "object" && !Array.isArray(v) && "choice" in v) {
        const parts = [`c=${v.choice}`];
        if (v.required_choices) {
          const rc = Object.entries(v.required_choices).filter(([, val]) => val && (!Array.isArray(val) || val.length))
            .map(([k, val]) => `${k}=${Array.isArray(val) ? [...val].sort().join("+") : val}`).sort();
          if (rc.length) parts.push(`rc=${rc.join(",")}`);
        }
        if (v.removals?.length) parts.push(`r=${[...v.removals].sort().join(",")}`);
        if (v.addons?.length) parts.push(`a=${[...v.addons].sort().join(",")}`);
        return `${gid}:{${parts.join(",")}}`;
      }
      // qty-style shape
      const parts = Object.keys(v).sort()
        .filter((oid) => (Number(v[oid]) || 0) > 0)
        .map((oid) => `${oid}=${v[oid]}`);
      return parts.length ? `${gid}:${parts.join(",")}` : "";
    })
    .filter(Boolean)
    .join("|");
};
const lineKeyFor = (itemId, selections) => {
  const sig = signature(selections);
  return sig ? `${itemId}#${sig}` : itemId;
};

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn("[cart] failed to load cart from localStorage", err);
    return {};
  }
}
function saveCart(cart) {
  try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }
  catch (err) { console.warn("[cart] failed to persist cart", err); }
}

// ----- Pricing helpers (mirror server-side math so cart total is honest) -----
const addonCents = (item, selections) => {
  if (!item.modifier_groups || !selections) return 0;
  let total = 0;
  for (const g of item.modifier_groups) {
    const v = selections[g.id];
    if (!v) continue;
    if (g.type === "slot") {
      const opt = (g.options || []).find((o) => o.id === v.choice);
      if (!opt) continue;
      total += Number(opt.price_cents || 0);
      // Multi-select required_choices (e.g. cheese): first `free_count` are
      // included, each additional selection adds its option's price_cents.
      for (const rc of opt.required_choices || []) {
        if (!rc.multi) continue;
        const ids = (v.required_choices || {})[rc.id];
        if (!Array.isArray(ids) || !ids.length) continue;
        const prices = ids
          .map((id) => (rc.options || []).find((o) => o.id === id))
          .filter(Boolean)
          .map((o) => Number(o.price_cents) || 0)
          .sort((a, b) => a - b);
        const freeN = rc.free_count || 0;
        for (let i = freeN; i < prices.length; i += 1) total += prices[i];
      }
      for (const aid of v.addons || []) {
        const a = (opt.addons || []).find((x) => x.id === aid);
        if (a) total += Number(a.price_cents || 0);
      }
      continue;
    }
    const opt_by_id = Object.fromEntries((g.options || []).map((o) => [o.id, o]));
    for (const [oid, q] of Object.entries(v)) {
      const qty = Number(q) || 0;
      if (qty <= 0) continue;
      total += Number(opt_by_id[oid]?.price_cents || 0) * qty;
    }
  }
  return total;
};
const unitCents = (item, selections) => Number(item.price_cents || 0) + addonCents(item, selections);

// Build cart rows joined with their item meta + computed pricing.
const buildLines = (cart, itemsById) =>
  Object.entries(cart)
    .map(([key, row]) => {
      const item = itemsById[row.id];
      if (!item) return null;
      const u = unitCents(item, row.selections);
      return {
        key,
        item,
        qty: row.qty,
        selections: row.selections || null,
        unit_cents: u,
        line_total_cents: u * row.qty,
      };
    })
    .filter(Boolean);

export default function Order() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => loadCart());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modifierFor, setModifierFor] = useState(null); // item being configured

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pickup/menu`);
        setItems(data);
      } catch (e) {
        toast.error("Couldn't load the pickup menu — please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => saveCart(cart), [cart]);

  const itemsById = useMemo(
    () => Object.fromEntries(items.map((it) => [it.id, it])),
    [items]
  );

  const addToCart = (item, selections) => {
    const key = lineKeyFor(item.id, selections);
    setCart((c) => {
      const existing = c[key];
      return {
        ...c,
        [key]: { id: item.id, qty: Math.min(99, (existing?.qty || 0) + 1), selections: selections || null },
      };
    });
  };
  const setLineQty = (key, qty) => {
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[key];
      else next[key] = { ...next[key], qty: Math.min(99, qty) };
      return next;
    });
  };

  const handleCardAdd = (item) => {
    if ((item.modifier_groups || []).length > 0) {
      setModifierFor(item);
    } else {
      addToCart(item, null);
    }
  };

  const byCategory = useMemo(() => {
    const groups = {};
    items.forEach((it) => { (groups[it.category] = groups[it.category] || []).push(it); });
    return groups;
  }, [items]);

  const lines = useMemo(() => buildLines(cart, itemsById), [cart, itemsById]);
  const totalCents = lines.reduce((s, l) => s + l.line_total_cents, 0);
  const totalQty = lines.reduce((s, l) => s + l.qty, 0);
  const anyUnpriced = lines.some((l) => l.unit_cents <= 0);

  // Per-item count badge on cards — sum of qty across all lines of that item.
  const itemQtyById = useMemo(() => {
    const out = {};
    lines.forEach((l) => { out[l.item.id] = (out[l.item.id] || 0) + l.qty; });
    return out;
  }, [lines]);

  return (
    <SiteContentProvider>
      <div className="bg-[#0B0B0B] text-white min-h-screen">
        <Header />
        <main className="pt-20 sm:pt-24" data-testid="order-page">
          <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 sm:py-14">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] sm:text-xs font-extrabold tracking-[0.4em] uppercase text-[#A3D900]">
                  PICKUP ORDER
                </p>
                <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
                  Order &<br className="sm:hidden" /> Pick Up
                </h1>
                <p className="mt-3 text-white/65 text-sm sm:text-base max-w-xl">
                  Curated party packs &amp; large sides from BTW — pay securely, pick
                  up fresh in Jamaica, Queens.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                data-testid="open-cart-btn"
                className="relative inline-flex items-center gap-2 rounded-full bg-[#5FBDD6] hover:bg-[#3FA8C4] text-[#0B0B0B] font-extrabold tracking-[0.16em] uppercase px-5 py-3 text-xs transition-colors shadow-[0_8px_30px_rgba(95,189,214,0.25)]"
              >
                <ShoppingCart size={16} />
                Cart
                {totalQty > 0 && (
                  <span data-testid="cart-badge" className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#A3D900] text-[#0B0B0B] text-[11px] font-extrabold border-2 border-[#0B0B0B]">
                    {totalQty}
                  </span>
                )}
              </button>
            </div>

            {loading && (
              <div className="mt-16 flex justify-center">
                <Loader2 className="animate-spin text-white/60" size={26} />
              </div>
            )}

            {!loading && (
              <div className="mt-10 space-y-12">
                {Object.entries(byCategory).map(([cat, group]) => (
                  <div key={cat} data-testid={`pickup-cat-${cat}`}>
                    <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white/90">
                      {CATEGORY_LABELS[cat] || cat}
                    </h2>
                    <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 justify-items-start" data-testid={`pickup-cat-${cat}-grid`}>
                      {group.map((it) => (
                        <ItemCard
                          key={it.id}
                          item={it}
                          totalQty={itemQtyById[it.id] || 0}
                          onAdd={() => handleCardAdd(it)}
                          onInc={() => setLineQty(it.id, (cart[it.id]?.qty || 0) + 1)}
                          onDec={() => setLineQty(it.id, (cart[it.id]?.qty || 0) - 1)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
        <Footer />
        <BackToTop />

        {/* Floating "View Cart" pill — shows when cart has items so customers
            don't have to scroll back up to find the header cart button. */}
        {totalQty > 0 && !drawerOpen && (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-testid="floating-view-cart-btn"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2.5 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-5 py-3 text-xs shadow-[0_12px_40px_rgba(163,217,0,0.35)] transition-colors"
          >
            <ShoppingCart size={16} />
            View Cart · {totalQty} · {fmt(totalCents)}
            <ArrowRight size={14} />
          </button>
        )}

        {modifierFor && (
          <ModifierModal
            item={modifierFor}
            onClose={() => setModifierFor(null)}
            onConfirm={(sel) => { addToCart(modifierFor, sel); setModifierFor(null); }}
          />
        )}

        <CartDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          lines={lines}
          totalCents={totalCents}
          anyUnpriced={anyUnpriced}
          onInc={(key) => setLineQty(key, (cart[key]?.qty || 0) + 1)}
          onDec={(key) => setLineQty(key, (cart[key]?.qty || 0) - 1)}
          onRemove={(key) => setLineQty(key, 0)}
          allItems={items}
          onAddItem={handleCardAdd}
        />
      </div>
    </SiteContentProvider>
  );
}

function ItemCard({ item, totalQty, onAdd, onInc, onDec }) {
  const unpriced = item.price_cents <= 0;
  const hasModifiers = (item.modifier_groups || []).length > 0;
  return (
    <article
      data-testid={`pickup-item-${item.id}`}
      className="bg-white text-[#0B0B0B] rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.35)] flex flex-col w-full lg:max-w-[320px]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#0B0B0B]">
        <img src={item.img} alt={item.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        {hasModifiers && totalQty > 0 && (
          <span className="absolute top-2 right-2 inline-flex items-center justify-center min-w-[26px] h-[26px] px-2 rounded-full bg-[#A3D900] text-[#0B0B0B] text-xs font-extrabold border-2 border-white" data-testid={`pickup-card-count-${item.id}`}>
            ×{totalQty}
          </span>
        )}
      </div>
      <div className="px-3 pt-3 pb-3 flex flex-col flex-1">
        <h3 className="font-display text-sm sm:text-base uppercase tracking-tight leading-tight">{item.name}</h3>
        {item.desc && (
          <p className="mt-1 text-[10px] sm:text-[11px] text-black/55 leading-snug line-clamp-2">{item.desc}</p>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between gap-2">
          <div
            className={`font-display text-lg sm:text-xl ${unpriced ? "text-black/40" : ""}`}
            style={{ color: unpriced ? undefined : "#5FBDD6" }}
            data-testid={`pickup-price-${item.id}`}
          >
            {unpriced ? "Coming soon" : fmt(item.price_cents)}
          </div>
          {!unpriced && hasModifiers && (
            <button
              type="button"
              onClick={onAdd}
              data-testid={`pickup-add-${item.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] active:bg-[#7aa800] text-[#0B0B0B] font-extrabold tracking-[0.14em] uppercase px-3 py-1.5 sm:px-4 sm:py-2.5 text-[10px] sm:text-xs shadow-sm hover:shadow-md transition-all"
            >
              <Plus size={12} className="sm:hidden" />
              <Plus size={14} className="hidden sm:inline" /> Customize
            </button>
          )}
          {!unpriced && !hasModifiers && totalQty === 0 && (
            <button
              type="button"
              onClick={onAdd}
              data-testid={`pickup-add-${item.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] active:bg-[#7aa800] text-[#0B0B0B] font-extrabold tracking-[0.14em] uppercase px-3 py-1.5 sm:px-4 sm:py-2.5 text-[10px] sm:text-xs shadow-sm hover:shadow-md transition-all"
            >
              <Plus size={12} className="sm:hidden" />
              <Plus size={14} className="hidden sm:inline" /> Add
            </button>
          )}
          {!unpriced && !hasModifiers && totalQty > 0 && (
            <div className="inline-flex items-center gap-1 rounded-full bg-[#0B0B0B] text-white px-1.5 py-1">
              <button
                type="button"
                onClick={onDec}
                data-testid={`pickup-dec-${item.id}`}
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white/10"
                aria-label="Decrease"
              >
                <Minus size={14} />
              </button>
              <span className="min-w-[18px] text-center text-xs font-extrabold tabular-nums" data-testid={`pickup-qty-${item.id}`}>{totalQty}</span>
              <button
                type="button"
                onClick={onInc}
                data-testid={`pickup-inc-${item.id}`}
                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white/10"
                aria-label="Increase"
              >
                <Plus size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function ModifierModal({ item, onClose, onConfirm }) {
  // Unified shape — slot groups use { choice, required_choices, removals, addons }
  // Other group types use { option_id: qty }
  const [selections, setSelections] = useState(() =>
    Object.fromEntries((item.modifier_groups || []).map((g) =>
      [g.id, g.type === "slot" ? { choice: null, required_choices: {}, removals: [], addons: [] } : {}]
    ))
  );

  const setSlot = (gid, patch) =>
    setSelections((s) => ({ ...s, [gid]: { ...s[gid], ...patch } }));

  const groupTotalQty = (gid) =>
    Object.values(selections[gid] || {}).reduce((s, q) => s + (Number(q) || 0), 0);
  const groupDistinct = (gid) =>
    Object.values(selections[gid] || {}).filter((q) => (Number(q) || 0) > 0).length;

  const setOptionQty = (gid, oid, q) => {
    const group = item.modifier_groups.find((g) => g.id === gid);
    const cur = { ...(selections[gid] || {}) };
    const currentDistinct = Object.values(cur).filter((qq) => qq > 0).length;
    const wasSelected = (cur[oid] || 0) > 0;
    if (q <= 0) {
      delete cur[oid];
    } else {
      // For multi groups, qty per option is always 1.
      if (group.type === "multi") {
        cur[oid] = 1;
      } else {
        cur[oid] = Math.min(999, q);
      }
    }
    const newDistinct = Object.values(cur).filter((qq) => qq > 0).length;
    const newTotal = Object.values(cur).reduce((s, qq) => s + qq, 0);

    // Enforce DISTINCT-options cap for allocation groups.
    if (group.type === "allocation" && group.max && newDistinct > group.max && !wasSelected) {
      toast.error(`Up to ${group.max} flavors`);
      return;
    }
    // Enforce TOTAL-qty cap for multi/qty groups (allocation has exact target, not cap).
    if (group.type !== "allocation" && group.max && newTotal > group.max) {
      toast.error(`Up to ${group.max} for ${group.label.toLowerCase()}`);
      return;
    }
    setSelections((s) => ({ ...s, [gid]: cur }));
  };

  const toggleMulti = (gid, oid) => {
    const cur = selections[gid] || {};
    setOptionQty(gid, oid, cur[oid] ? 0 : 1);
  };
  const incQty = (gid, oid, step = 1) => {
    const cur = (selections[gid] || {})[oid] || 0;
    setOptionQty(gid, oid, cur + step);
  };
  const decQty = (gid, oid, step = 1) => {
    const cur = (selections[gid] || {})[oid] || 0;
    setOptionQty(gid, oid, cur - step);
  };

  const valid = (item.modifier_groups || []).every((g) => {
    if (g.type === "slot") {
      const v = selections[g.id];
      if (!v || !v.choice) return false;
      const opt = (g.options || []).find((o) => o.id === v.choice);
      if (!opt) return false;
      for (const rc of opt.required_choices || []) {
        const val = (v.required_choices || {})[rc.id];
        if (rc.multi) {
          const n = Array.isArray(val) ? val.length : 0;
          if (n < (rc.min || 1)) return false;
        } else if (!val) {
          return false;
        }
      }
      return true;
    }
    if (g.type === "allocation") {
      const distinct = groupDistinct(g.id);
      const total = groupTotalQty(g.id);
      return distinct >= (g.min || 0) && (!g.max || distinct <= g.max) && total === (g.total || 0);
    }
    const n = groupTotalQty(g.id);
    return n >= (g.min || 0) && (!g.max || n <= g.max);
  });
  const previewUnit = unitCents(item, selections);

  return (
    <div className="fixed inset-0 z-[70]" data-testid="modifier-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} role="presentation" />
      <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:m-auto sm:h-fit sm:max-w-2xl lg:max-w-4xl bg-[#0B0B0B] border border-white/10 sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh]">
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="font-display text-xl uppercase tracking-tight">{item.name}</h2>
            <p className="text-[11px] text-white/55">Customize and add to cart</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="modifier-close-btn" className="p-2 -mr-2 hover:bg-white/5 rounded-full"><X size={18} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {(item.modifier_groups || []).map((g) => {
            if (g.type === "slot") {
              return (
                <SlotPicker
                  key={g.id}
                  group={g}
                  value={selections[g.id]}
                  onChange={(patch) => setSlot(g.id, patch)}
                />
              );
            }
            const total = groupTotalQty(g.id);
            const distinct = groupDistinct(g.id);
            const step = g.step || 1;
            const target = g.total || 0;
            const remaining = Math.max(0, target - total);
            const overshot = total > target && target > 0;
            const headerHint =
              g.type === "allocation"
                ? `Required · pick ${g.min === g.max ? g.min : `${g.min}–${g.max}`} flavors · ${target} total`
                : g.min > 0
                  ? `Required · pick at least ${g.min}${g.max ? `, up to ${g.max}` : ""}`
                  : g.type === "qty"
                    ? "Optional · any quantity"
                    : g.max ? `Optional · up to ${g.max}` : "Optional";
            return (
              <div key={g.id} data-testid={`modifier-group-${g.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-display text-base uppercase tracking-tight">{g.label}</h3>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">{headerHint}</span>
                </div>

                {g.type === "allocation" && (
                  <AllocationProgress
                    total={total}
                    target={target}
                    remaining={remaining}
                    overshot={overshot}
                    distinct={distinct}
                    maxDistinct={g.max}
                  />
                )}

                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {(g.options || []).map((o) => {
                    const qty = (selections[g.id] || {})[o.id] || 0;
                    if (g.type === "qty") {
                      return <QtyOptionTile key={o.id} option={o} qty={qty} step={1} onInc={() => incQty(g.id, o.id, 1)} onDec={() => decQty(g.id, o.id, 1)} testid={`modifier-option-${g.id}-${o.id}`} />;
                    }
                    if (g.type === "allocation") {
                      const reachedMax = !qty && g.max && distinct >= g.max;
                      const blockInc = (target > 0 && total >= target);
                      return <AllocationOptionTile key={o.id} option={o} qty={qty} step={step} disableInc={blockInc || reachedMax} onInc={() => incQty(g.id, o.id, step)} onDec={() => decQty(g.id, o.id, step)} testid={`modifier-option-${g.id}-${o.id}`} />;
                    }
                    return <MultiOptionTile key={o.id} option={o} picked={Boolean(qty)} onToggle={() => toggleMulti(g.id, o.id)} testid={`modifier-option-${g.id}-${o.id}`} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <footer className="border-t border-white/10 px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">Item total</div>
            <div className="font-display text-2xl" style={{ color: "#5FBDD6" }} data-testid="modifier-unit-total">{fmt(previewUnit)}</div>
          </div>
          <button
            type="button"
            disabled={!valid}
            onClick={() => onConfirm(selections)}
            data-testid="modifier-confirm-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] disabled:opacity-40 disabled:cursor-not-allowed text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-xs transition-colors"
          >
            <Plus size={13} /> Add to cart
          </button>
        </footer>
      </div>
    </div>
  );
}

function MultiOptionTile({ option, picked, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`text-left rounded-xl px-3 py-2.5 border-2 transition-colors ${picked ? "bg-[#5FBDD6] text-[#0B0B0B] border-[#5FBDD6]" : "bg-white/[0.04] text-white/85 border-white/10 hover:border-white/30"}`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${picked ? "border-[#0B0B0B] bg-[#0B0B0B]" : "border-white/40"}`}>
          {picked && <Check size={11} className="text-[#A3D900]" />}
        </span>
        <span className="text-xs font-extrabold uppercase tracking-[0.08em] leading-tight">{option.label}</span>
      </div>
      {option.price_cents > 0 && (
        <div className={`mt-0.5 ml-6 text-[11px] ${picked ? "text-[#0B0B0B]/70" : "text-[#A3D900]"}`}>+{fmt(option.price_cents)}</div>
      )}
    </button>
  );
}

function QtyOptionTile({ option, qty, step = 1, onInc, onDec, testid }) {
  const picked = qty > 0;
  return (
    <div
      data-testid={testid}
      className={`rounded-xl px-3 py-2.5 border-2 transition-colors flex flex-col gap-2 ${picked ? "bg-white text-[#0B0B0B] border-white" : "bg-white/[0.04] text-white/85 border-white/10"}`}
    >
      <div>
        <div className="text-xs font-extrabold uppercase tracking-[0.08em] leading-tight">{option.label}</div>
        {option.price_cents > 0 && (
          <div className={`text-[11px] ${picked ? "text-[#0B0B0B]/65" : "text-[#A3D900]"}`}>+{fmt(option.price_cents)} each</div>
        )}
      </div>
      <div className={`inline-flex items-center justify-between gap-1.5 rounded-full ${picked ? "bg-[#0B0B0B] text-white" : "bg-white/10 text-white"} px-1.5 py-1`}>
        <button
          type="button"
          onClick={onDec}
          disabled={qty === 0}
          data-testid={`${testid}-dec`}
          className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-30"
          aria-label="Decrease"
        >
          <Minus size={12} />
        </button>
        <span className="min-w-[18px] text-center text-xs font-extrabold" data-testid={`${testid}-qty`}>{qty}</span>
        <button
          type="button"
          onClick={onInc}
          data-testid={`${testid}-inc`}
          className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-white/10"
          aria-label="Increase"
        >
          <Plus size={12} />
        </button>
      </div>
      {step > 1 && (
        <span className={`text-[10px] uppercase tracking-[0.14em] ${picked ? "text-[#0B0B0B]/55" : "text-white/45"}`}>+/- {step}</span>
      )}
    </div>
  );
}

function AllocationOptionTile({ option, qty, step, disableInc, onInc, onDec, testid }) {
  const picked = qty > 0;
  return (
    <div
      data-testid={testid}
      className={`rounded-xl px-3 py-2.5 border-2 transition-colors flex flex-col gap-2 ${picked ? "bg-[#5FBDD6] text-[#0B0B0B] border-[#5FBDD6]" : "bg-white/[0.04] text-white/85 border-white/10"}`}
    >
      <div className="text-xs font-extrabold uppercase tracking-[0.08em] leading-tight">{option.label}</div>
      <div className={`inline-flex items-center justify-between gap-1.5 rounded-full ${picked ? "bg-[#0B0B0B] text-white" : "bg-white/10 text-white"} px-1.5 py-1`}>
        <button
          type="button"
          onClick={onDec}
          disabled={qty === 0}
          data-testid={`${testid}-dec`}
          className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-30"
          aria-label="Decrease"
        >
          <Minus size={12} />
        </button>
        <span className="min-w-[28px] text-center text-xs font-extrabold tabular-nums" data-testid={`${testid}-qty`}>{qty}</span>
        <button
          type="button"
          onClick={onInc}
          disabled={disableInc}
          data-testid={`${testid}-inc`}
          className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-30"
          aria-label="Increase"
        >
          <Plus size={12} />
        </button>
      </div>
      <span className={`text-[10px] uppercase tracking-[0.14em] ${picked ? "text-[#0B0B0B]/55" : "text-white/45"}`}>+/- {step} wings</span>
    </div>
  );
}

function AllocationProgress({ total, target, remaining, overshot, distinct, maxDistinct }) {
  const pct = target ? Math.min(100, Math.round((total / target) * 100)) : 0;
  const done = total === target && target > 0;
  return (
    <div className="mt-3 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5" data-testid="allocation-progress">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-display text-sm sm:text-base">
          <span className={`tabular-nums ${overshot ? "text-red-400" : done ? "text-[#A3D900]" : "text-white"}`} data-testid="alloc-total">{total}</span>
          <span className="text-white/45"> / {target} wings</span>
        </div>
        <div className={`text-[10px] uppercase tracking-[0.18em] ${done ? "text-[#A3D900]" : overshot ? "text-red-400" : "text-yellow-300"}`} data-testid="alloc-remaining">
          {done ? "Ready" : overshot ? `Remove ${total - target}` : `${remaining} to assign`}
        </div>
      </div>
      <div className="mt-2 h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div
          className={`h-full transition-all ${overshot ? "bg-red-400" : done ? "bg-[#A3D900]" : "bg-[#5FBDD6]"}`}
          style={{ width: `${pct}%` }}
          data-testid="alloc-progress-bar"
        />
      </div>
      {maxDistinct && (
        <div className="mt-1.5 text-[10px] text-white/45">
          Flavors picked: <span className="text-white/75 tabular-nums" data-testid="alloc-distinct">{distinct}</span> / {maxDistinct}
        </div>
      )}
    </div>
  );
}

function summariseSelections(item, selections) {
  if (!selections || !item.modifier_groups) return null;
  return item.modifier_groups
    .map((g) => {
      const v = selections[g.id];
      if (!v) return null;
      // slot shape
      if (g.type === "slot") {
        const opt = (g.options || []).find((o) => o.id === v.choice);
        if (!opt) return null;
        const bits = [opt.label];
        for (const rc of opt.required_choices || []) {
          const cid = (v.required_choices || {})[rc.id];
          if (rc.multi) {
            const ids = Array.isArray(cid) ? cid : [];
            const cos = ids.map((id) => (rc.options || []).find((o) => o.id === id)).filter(Boolean);
            if (cos.length) {
              // Price summary: subtract `free_count` cheapest cheese prices.
              const prices = cos.map((o) => Number(o.price_cents) || 0).sort((a, b) => a - b);
              const freeN = rc.free_count || 0;
              const extra = prices.slice(freeN).reduce((s, p) => s + p, 0);
              const labels = cos.map((o) => o.label).join(", ");
              bits.push(`(${rc.label}: ${labels}${extra ? ` (+$${(extra / 100).toFixed(0)} extra)` : ""})`);
            }
          } else {
            const co = (rc.options || []).find((o) => o.id === cid);
            if (co) bits.push(`(${rc.label}: ${co.label})`);
          }
        }
        const removedLabels = (v.removals || [])
          .map((rid) => (opt.removals || []).find((r) => r.id === rid)?.label)
          .filter(Boolean);
        if (removedLabels.length) bits.push(`NO ${removedLabels.join(", ")}`);
        const addonLabels = (v.addons || [])
          .map((aid) => (opt.addons || []).find((a) => a.id === aid)?.label)
          .filter(Boolean);
        if (addonLabels.length) bits.push(`ADD ${addonLabels.join(", ")}`);
        return { label: g.label, values: [bits.join(" · ")] };
      }
      // qty-style
      const picked = (g.options || [])
        .map((o) => {
          const q = Number(v[o.id]) || 0;
          if (q <= 0) return null;
          return (g.type === "qty" || g.type === "allocation") ? `${q}× ${o.label}` : o.label;
        })
        .filter(Boolean);
      if (!picked.length) return null;
      return { label: g.label, values: picked };
    })
    .filter(Boolean);
}

function CartDrawer({ open, onClose, lines, totalCents, anyUnpriced, onInc, onDec, onRemove, allItems, onAddItem }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pickupAt, setPickupAt] = useState("");
  const [busy, setBusy] = useState(false);
  // Customers should be able to dismiss the upsell so it doesn't dominate the
  // small cart drawer on mobile. State resets when the drawer is closed and
  // reopened, so the suggestion gets one fresh impression per session.
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  useEffect(() => {
    if (open) setSuggestionsDismissed(false);
  }, [open]);

  // Suggested add-ons: priced, in-stock, one-tap (no modifiers), not already
  // in the cart. We surface up to 3 — chosen in the menu's curated order so
  // the owner controls what gets pushed first by reordering items in the CMS.
  const inCart = useMemo(
    () => new Set(lines.map((l) => l.item.id)),
    [lines]
  );
  const suggestions = useMemo(() => {
    if (!allItems || !lines.length) return [];
    return allItems
      .filter((it) => it.active !== false)
      .filter((it) => (it.price_cents || 0) > 0)
      .filter((it) => !(it.modifier_groups && it.modifier_groups.length))
      .filter((it) => !inCart.has(it.id))
      .slice(0, 3);
  }, [allItems, inCart, lines]);

  // Operating hours (store local time):
  //   Mon: CLOSED  ·  Tue–Thu, Sun: 11am–7pm  ·  Fri–Sat: 11am–8pm
  // Returns { ok, reason } when a pickup datetime is invalid.
  const validatePickup = (s) => {
    if (!s) return { ok: false, reason: "" };
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return { ok: false, reason: "Invalid date" };
    const wd = d.getDay(); // 0=Sun … 6=Sat
    if (wd === 1) return { ok: false, reason: "We're closed Mondays — please pick another day." };
    const closeHour = (wd === 5 || wd === 6) ? 20 : 19;
    const closeLabel = closeHour === 20 ? "8pm" : "7pm";
    const m = d.getHours() * 60 + d.getMinutes();
    if (m < 11 * 60 || m > closeHour * 60) {
      return { ok: false, reason: `We're open 11am–${closeLabel} that day. Please pick another time.` };
    }
    return { ok: true };
  };
  const pickupCheck = validatePickup(pickupAt);
  const pickupBlocked = pickupAt !== "" && !pickupCheck.ok;

  const minPickup = useMemo(() => {
    if (!lines.length) return null;
    const maxLead = Math.max(...lines.map((l) => l.item.lead_hours || 1));
    const d = new Date(Date.now() + maxLead * 3600 * 1000);
    // Snap to the next valid open slot — skip Mondays + pre-open hours.
    const snap = (dt) => {
      // If it's Monday, jump to Tuesday 11am.
      if (dt.getDay() === 1) {
        dt.setDate(dt.getDate() + 1);
        dt.setHours(11, 0, 0, 0);
        return;
      }
      const closeHour = (dt.getDay() === 5 || dt.getDay() === 6) ? 20 : 19;
      // Before 11am → set to 11:00 same day.
      if (dt.getHours() < 11) { dt.setHours(11, 0, 0, 0); return; }
      // After close → next day at 11am.
      if (dt.getHours() * 60 + dt.getMinutes() > closeHour * 60) {
        dt.setDate(dt.getDate() + 1);
        dt.setHours(11, 0, 0, 0);
        snap(dt); // re-check (in case next day is Monday)
      }
    };
    snap(d);
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [lines]);

  const submit = async (e) => {
    e.preventDefault();
    if (!lines.length) { toast.error("Your cart is empty."); return; }
    if (anyUnpriced) { toast.error("One or more items aren't priced yet — remove them to continue."); return; }
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    if (!phone.trim()) { toast.error("Please enter a phone number for pickup notifications."); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email for your order confirmation.");
      return;
    }
    if (!pickupAt) { toast.error("Please choose a pickup date & time."); return; }
    if (pickupBlocked) { toast.error(pickupCheck.reason); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        items: lines.map((l) => ({ id: l.item.id, qty: l.qty, selections: l.selections || undefined })),
        origin_url: window.location.origin,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        customer_email: email.trim(),
        pickup_at: pickupAt,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Could not start checkout. Please try again.");
    } catch (err) {
      // Surface every fragment we can find so prod debugging isn't a black box.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const code = err?.code;
      console.error("[checkout] failed", { status, code, detail, err });
      const msg = detail
        ? `${detail}`
        : status
          ? `Checkout failed (HTTP ${status}). Try again.`
          : code === "ERR_NETWORK"
            ? "Network error — please check your connection and try again."
            : "Checkout failed. Try again.";
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[60] transition-opacity ${open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
      aria-hidden={!open}
      data-testid="cart-drawer"
    >
      <div className="absolute inset-0 bg-black/70" onClick={onClose} role="presentation" />
      <aside className={`absolute right-0 top-0 h-full w-full sm:w-[440px] bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}>
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="font-display text-xl uppercase tracking-tight">Your Cart</h2>
          <button onClick={onClose} aria-label="Close cart" data-testid="close-cart-btn" className="p-2 -mr-2 hover:bg-white/5 rounded-full"><X size={20} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {!lines.length && <p className="text-white/55 text-sm">Your cart is empty.</p>}
          {lines.map((l) => {
            const summary = summariseSelections(l.item, l.selections);
            return (
              <div key={l.key} className="flex gap-3 items-start" data-testid={`cart-line-${l.key}`}>
                <img src={l.item.img} alt={l.item.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm uppercase leading-tight">{l.item.name}</div>
                  <div className="text-[11px] text-white/55 mt-0.5">{fmt(l.unit_cents)} each</div>
                  {summary && summary.length > 0 && (
                    <ul className="mt-1 text-[10px] text-white/65 space-y-0.5">
                      {summary.map((g) => (
                        <li key={g.label}><span className="text-white/40">{g.label}:</span> {g.values.join(", ")}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-1.5 py-1">
                    <button onClick={() => onDec(l.key)} className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-white/10" aria-label="Decrease"><Minus size={12} /></button>
                    <span className="min-w-[16px] text-center text-xs font-extrabold">{l.qty}</span>
                    <button onClick={() => onInc(l.key)} className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-white/10" aria-label="Increase"><Plus size={12} /></button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-sm" style={{ color: "#5FBDD6" }}>{fmt(l.line_total_cents)}</div>
                  <button onClick={() => onRemove(l.key)} className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/45 hover:text-white">Remove</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Upsell — surfaces high-margin sides the customer doesn't have yet.
            Renders only when cart has items, there's at least one eligible
            suggestion, AND the customer hasn't dismissed it for this session. */}
        {lines.length > 0 && suggestions.length > 0 && !suggestionsDismissed && (
          <div className="border-t border-white/10 px-5 py-3" data-testid="cart-suggestions">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/55">
                You might also like
              </div>
              <button
                type="button"
                onClick={() => setSuggestionsDismissed(true)}
                data-testid="cart-suggestions-dismiss"
                className="-mr-1 p-1.5 text-white/50 hover:text-white/90 hover:bg-white/5 rounded-full transition-colors"
                aria-label="Dismiss suggestions"
              >
                <X size={14} />
              </button>
            </div>
            {/* Horizontal scroll keeps the tiles compact on mobile — they
                don't dominate the screen, customer can swipe to browse. */}
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onAddItem && onAddItem(s)}
                  data-testid={`cart-suggestion-${s.id}`}
                  className="group shrink-0 w-[112px] snap-start flex flex-col items-stretch rounded-lg bg-white/[0.04] border border-white/10 hover:border-[#A3D900]/60 hover:bg-white/[0.07] transition-colors overflow-hidden text-left"
                >
                  <div className="aspect-square w-full overflow-hidden bg-black/40">
                    <img src={s.img} alt={s.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                  </div>
                  <div className="px-2 pt-1.5 pb-2">
                    <div className="font-display text-[10px] uppercase tracking-tight leading-tight line-clamp-2 min-h-[24px]">{s.name}</div>
                    <div className="mt-1 flex items-center justify-between gap-1">
                      <span className="font-display text-[11px]" style={{ color: "#5FBDD6" }}>{fmt(s.price_cents)}</span>
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#A3D900]">
                        <Plus size={9} /> Add
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {lines.length > 0 && (
          <form onSubmit={submit} className="border-t border-white/10 px-5 py-4 space-y-3" data-testid="checkout-form">
            <div className="grid grid-cols-2 gap-2.5">
              <label className="block">
                <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-white/55 mb-1">Name</span>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)} data-testid="cart-name"
                  className="w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3 py-2 text-base placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6]" />
              </label>
              <label className="block">
                <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-white/55 mb-1">Phone</span>
                <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="cart-phone"
                  className="w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3 py-2 text-base placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6]" />
              </label>
            </div>
            <label className="block">
              <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-white/55 mb-1">Email <span className="text-white/35 normal-case tracking-normal text-[10px]">(for your order confirmation)</span></span>
              <input type="email" required autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cart-email"
                placeholder="you@example.com"
                className="w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3 py-2 text-base placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6]" />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold tracking-[0.18em] uppercase text-white/55 mb-1">Pickup date & time</span>
              <input type="datetime-local" required min={minPickup || undefined} value={pickupAt} onChange={(e) => setPickupAt(e.target.value)} data-testid="cart-pickup-at"
                className={`w-full rounded-lg bg-white/[0.06] border text-white px-3 py-2 text-base focus:outline-none ${pickupBlocked ? "border-red-500/70 focus:border-red-500" : "border-white/15 focus:border-[#5FBDD6]"}`} />
              {pickupBlocked ? (
                <span className="block text-[10px] text-red-300 mt-1" data-testid="cart-monday-closed">
                  {pickupCheck.reason}
                </span>
              ) : (
                <span className="block text-[10px] text-white/40 mt-1">
                  <span className="text-white/55">Open Tue–Sun · 11am–7pm</span> <span className="text-white/30">(Fri & Sat till 8pm · Closed Mondays)</span>
                </span>
              )}
            </label>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs uppercase tracking-[0.18em] text-white/55">Total</span>
              <span className="font-display text-2xl" style={{ color: "#5FBDD6" }} data-testid="cart-total">{fmt(totalCents)}</span>
            </div>
            <button type="submit" disabled={busy || anyUnpriced || pickupBlocked} data-testid="checkout-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] disabled:opacity-60 text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3.5 text-sm transition-colors">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {busy ? "Starting checkout…" : "Pay & Confirm"}
            </button>
            <button type="button" onClick={onClose} data-testid="continue-shopping-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-transparent border border-white/20 hover:border-white/40 text-white/85 hover:text-white font-extrabold tracking-[0.18em] uppercase px-6 py-2.5 text-[11px] transition-colors">
              Continue Shopping
            </button>
            <p className="text-[10px] text-white/40 text-center">Secure checkout by Stripe · Cards accepted · Pickup-only</p>
          </form>
        )}
      </aside>
    </div>
  );
}


// ============================================================
// SLOT PICKER — used for 12 Mix Sliders & 12 Mixed Tacos
// Each slot has: choice (1 of N), optional required sub-choices (cheese / heat),
// removals (uncheck default toppings), and optional add-ons.
// ============================================================
function SlotPicker({ group, value, onChange }) {
  const v = value || { choice: null, required_choices: {}, removals: [], addons: [] };
  const picked = (group.options || []).find((o) => o.id === v.choice) || null;

  const chooseOption = (oid) => {
    if (oid === v.choice) return;
    onChange({ choice: oid, required_choices: {}, removals: [], addons: [] });
  };
  const setRequired = (rcId, optId) =>
    onChange({ required_choices: { ...(v.required_choices || {}), [rcId]: optId } });
  const toggleRemoval = (id) => {
    const cur = new Set(v.removals || []);
    cur.has(id) ? cur.delete(id) : cur.add(id);
    onChange({ removals: Array.from(cur) });
  };
  const toggleAddon = (id) => {
    const cur = new Set(v.addons || []);
    cur.has(id) ? cur.delete(id) : cur.add(id);
    onChange({ addons: Array.from(cur) });
  };

  return (
    <div data-testid={`modifier-group-${group.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display text-base uppercase tracking-tight">{group.label}</h3>
        <span className={`text-[10px] uppercase tracking-[0.18em] ${picked ? "text-white/45" : "text-yellow-300"}`}>
          {picked ? "Picked" : "Required"}
        </span>
      </div>

      <div className="mt-3">
        <select
          value={v.choice || ""}
          onChange={(e) => chooseOption(e.target.value)}
          data-testid={`slot-select-${group.id}`}
          className="w-full rounded-lg bg-white/[0.06] border border-white/15 text-white px-3 py-3 text-sm font-extrabold uppercase tracking-[0.08em] focus:outline-none focus:border-[#5FBDD6] appearance-none"
        >
          <option value="" disabled>Choose…</option>
          {(group.options || []).map((o) => (
            <option key={o.id} value={o.id} className="bg-[#0B0B0B]">{o.label}</option>
          ))}
        </select>
      </div>

      {picked && (
        <div className="mt-3 rounded-xl bg-white/[0.04] border border-white/10 p-3 space-y-3" data-testid={`slot-detail-${group.id}`}>
          {picked.desc && (
            <p className="text-[11px] text-white/55">{picked.desc}</p>
          )}

          {(picked.required_choices || []).map((rc) => {
            const isMulti = !!rc.multi;
            const sel = (v.required_choices || {})[rc.id];
            const selected = isMulti ? (Array.isArray(sel) ? sel : []) : sel;
            const toggleMulti = (optId) => {
              const cur = new Set(selected);
              cur.has(optId) ? cur.delete(optId) : cur.add(optId);
              setRequired(rc.id, Array.from(cur));
            };
            return (
              <div key={rc.id}>
                <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-white/65 mb-1.5">
                  {rc.label} <span className="text-yellow-300">· required</span>
                  {isMulti && (
                    <span className="text-white/40 normal-case tracking-normal"> · pick one or more</span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {rc.options.map((opt) => {
                    const isOn = isMulti ? selected.includes(opt.id) : selected === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => (isMulti ? toggleMulti(opt.id) : setRequired(rc.id, opt.id))}
                        data-testid={`slot-rc-${group.id}-${rc.id}-${opt.id}`}
                        className={`text-left rounded-lg px-3 py-2 border-2 transition-colors text-xs font-extrabold uppercase tracking-[0.08em] ${
                          isOn ? "bg-[#5FBDD6] text-[#0B0B0B] border-[#5FBDD6]"
                              : "bg-white/[0.04] text-white/85 border-white/10 hover:border-white/30"
                        }`}
                      >
                        {isMulti && <span className="mr-1">{isOn ? "✓" : "+"}</span>}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {(picked.removals || []).length > 0 && (
            <div>
              <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-white/65 mb-1.5">
                Default toppings <span className="text-white/40">· tap to remove</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {picked.removals.map((r) => {
                  const removed = (v.removals || []).includes(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => toggleRemoval(r.id)}
                      data-testid={`slot-removal-${group.id}-${r.id}`}
                      className={`text-left rounded-lg px-3 py-2 border-2 transition-colors text-xs font-bold ${
                        removed
                          ? "bg-red-500/15 text-red-200 border-red-500/40 line-through decoration-2"
                          : "bg-white/[0.04] text-white/85 border-white/10 hover:border-white/30"
                      }`}
                    >
                      {removed ? "✕ " : "✓ "}{r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(picked.addons || []).length > 0 && (
            <div>
              <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-white/65 mb-1.5">
                Add-ons <span className="text-white/40">· optional</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {picked.addons.map((a) => {
                  const on = (v.addons || []).includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAddon(a.id)}
                      data-testid={`slot-addon-${group.id}-${a.id}`}
                      className={`text-left rounded-lg px-3 py-2 border-2 transition-colors text-xs font-extrabold uppercase tracking-[0.08em] ${
                        on ? "bg-[#A3D900] text-[#0B0B0B] border-[#A3D900]"
                           : "bg-white/[0.04] text-white/85 border-white/10 hover:border-white/30"
                      }`}
                    >
                      {on ? "✓ " : "+ "}{a.label}
                      {a.price_cents > 0 && (
                        <span className={`block mt-0.5 text-[10px] font-bold ${on ? "text-[#0B0B0B]/70" : "text-[#A3D900]"}`}>
                          +{fmt(a.price_cents)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

